import { makeAutoObservable, reaction, runInAction } from "mobx";

import { AccountStore } from "./AccountStore";
import { BalanceStore } from "./BalanceStore";
import { ConfigStore } from "./ConfigStore";
import { DialogStore } from "./DialogStore";
import { GeoFenceStore } from "./GeoFenceStore";
import { MarketStore } from "./MarketStore";
import { MyOrdersStore } from "./MyOrdersStore";
import { OrderbookStore } from "./OrderbookStore";
import { SubAccountsStore } from "./SubAccountsStore";
import { ToastStore } from "./ToastStore";
import { TokenStore } from "./TokenStore";
import { TradeboxStore } from "./TradeboxStore";
import { TradesStore } from "./TradesStore";
import { VaultStore } from "./VaultStore";

export class RootStore {
  static instance?: RootStore;

  configStore: ConfigStore;
  geoFenceStore: GeoFenceStore;
  dialogStore: DialogStore;
  tokenStore: TokenStore;
  subAccountsStore: SubAccountsStore;
  accountStore: AccountStore;
  orderbookStore: OrderbookStore;
  marketStore: MarketStore;
  tradesStore: TradesStore;
  balanceStore: BalanceStore;
  vaultStore: VaultStore;
  toastStore: ToastStore;
  tradeboxStore: TradeboxStore;
  myOrdersStore: MyOrdersStore;

  isAppReady = false;

  private constructor() {
    this.configStore = new ConfigStore();
    this.geoFenceStore = new GeoFenceStore();
    this.dialogStore = new DialogStore();
    this.tokenStore = new TokenStore(this);
    this.subAccountsStore = new SubAccountsStore(this);
    this.accountStore = new AccountStore(this);
    this.orderbookStore = new OrderbookStore();
    this.marketStore = new MarketStore(this);
    this.tradesStore = new TradesStore(this);
    this.balanceStore = new BalanceStore(this);
    this.vaultStore = new VaultStore(this);
    this.toastStore = new ToastStore();
    this.tradeboxStore = new TradeboxStore(this);
    this.myOrdersStore = new MyOrdersStore(this);

    makeAutoObservable(this);

    reaction(
      () => this.accountStore.wallet?.account?.address,
      (walletValue, oldWalletValue) => {
        if (
          (walletValue === undefined && oldWalletValue !== null) ||
          (walletValue === null && oldWalletValue !== null)
        ) {
          this.clear();
        }
      },
    );

    this.initialize();
  }

  private async clear() {
    this.tokenStore.reset();
    this.subAccountsStore.reset();
    this.accountStore.reset();
    this.balanceStore.reset();
    this.vaultStore.reset();
    this.tradeboxStore.reset();
    this.myOrdersStore.reset();
  }

  private async initialize() {
    try {
      await this.tokenStore.initialize();
      await this.subAccountsStore.initialize();
      await this.accountStore.initialize();
      this.geoFenceStore.initialize();
    } catch (error) {
      this.toastStore.show({
        type: "error",
        title: "Oops!",
        content: "Something went wrong while loading. Please refresh the page. Check the console for details.",
      });
      console.error("Error initializing stores:", error);
      this.clear();
    } finally {
      runInAction(() => {
        this.isAppReady = true;
      });
    }
  }

  static create = () => {
    if (!RootStore.instance) {
      RootStore.instance = new RootStore();
    }

    return RootStore.instance;
  };
}
